import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Plus, Search, Edit, Trash2, Calendar, Loader2, X, Upload, Tag, Save, Clock } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { getCategoryName } from '../../utils/categoryUtils';
import RichTextBlockEditor from './events/blocks/RichTextBlockEditor';
import { getApiBaseUrl } from '../../utils/apiUrl';

const EventsManagementUnified = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('events'); // 'events' or 'categories'
  
  // Shared notification
  const [notification, setNotification] = useState(null);
  
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Events & Categories Management</h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage events and their categories
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('events')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'events'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Calendar className="inline w-4 h-4 mr-2" />
              Events
            </button>
            <button
              onClick={() => setActiveTab('categories')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'categories'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Tag className="inline w-4 h-4 mr-2" />
              Categories
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'events' ? (
          <EventsTab showNotification={showNotification} />
        ) : (
          <CategoriesTab showNotification={showNotification} />
        )}
      </div>

      {/* Notification */}
      {notification && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${
            notification.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}
        >
          {notification.message}
        </motion.div>
      )}
    </div>
  );
};

// Events Tab Component
const EventsTab = ({ showNotification }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterActive, setFilterActive] = useState('active'); // 'active', 'inactive', 'all'
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);

  // Fetch categories for filter dropdown
  const fetchCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      console.log('🔍 Fetching categories from API...');
      const response = await fetch(`${getApiBaseUrl()}/api/admin/categories?include_inactive=false`, {
        headers,
        cache: 'no-cache'
      });

      console.log('📡 Categories API response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('📦 Categories API response data:', data);
        if (data.success) {
          const categoriesArray = data.data || [];
          console.log(`✅ Loaded ${categoriesArray.length} categories:`, categoriesArray.map(c => c.name_en));
          setCategories(categoriesArray);
        } else {
          console.error('❌ API returned success=false:', data);
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Categories API error:', response.status, errorText);
      }
    } catch (err) {
      console.error('❌ Error fetching categories:', err);
    }
  }, []);

  // Fetch events
  const fetchEvents = useCallback(async (page = 1, search = '', category = '', activeFilter = 'active') => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.itemsPerPage.toString(),
        search,
        category
      });
      
      // Only include drafts if showing all or inactive
      // If showing only active, don't include drafts (backend will filter is_active = 1)
      if (activeFilter === 'all' || activeFilter === 'inactive') {
        params.append('include_drafts', 'true');
      }

      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${getApiBaseUrl()}/api/events?${params}`, {
        headers,
        cache: 'no-cache'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        let eventsArray = data.data?.events || data.data || [];
        
        // Client-side filter for active/inactive if needed
        if (activeFilter === 'active') {
          eventsArray = eventsArray.filter(e => e.is_active === 1 || e.is_active === true);
        } else if (activeFilter === 'inactive') {
          eventsArray = eventsArray.filter(e => e.is_active === 0 || e.is_active === false);
        }
        // 'all' shows everything (no filter)
        
        // Filter out past events (only show future/current events)
        // Unless user explicitly wants to see all (including past)
        if (activeFilter === 'active') {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          eventsArray = eventsArray.filter(e => {
            if (!e.end_date) return true; // No end date, show it
            const endDate = new Date(e.end_date);
            endDate.setHours(23, 59, 59, 999);
            return endDate >= today; // Only show if not ended
          });
        }
        
        setEvents(Array.isArray(eventsArray) ? eventsArray : []);
        setPagination(prev => ({
          ...prev,
          currentPage: data.data?.pagination?.currentPage || page,
          totalPages: data.data?.pagination?.totalPages || 1,
          totalItems: data.data?.pagination?.totalItems || 0
        }));

        const totalPages = data.data?.pagination?.totalPages || 1;
        const currentPage = data.data?.pagination?.currentPage || page;
        const validPage = totalPages > 0 ? Math.min(Math.max(currentPage, 1), totalPages) : 1;
        
        if (validPage !== currentPage) {
          fetchEvents(validPage, search, category, activeFilter);
          return;
        }
      } else {
        setError(data.error || 'Failed to fetch events');
        setEvents([]);
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Network error: ' + err.message);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.itemsPerPage]);

  useEffect(() => {
    fetchCategories();
    fetchEvents(1, searchTerm, filterCategory, filterActive);
  }, [fetchCategories]);

  // Debug: Log categories when they change
  useEffect(() => {
    console.log('📋 Categories state updated:', categories.length, 'categories');
    if (categories.length > 0) {
      console.log('📋 Categories:', categories.map(c => ({ id: c.id, name_en: c.name_en, name_th: c.name_th })));
    }
  }, [categories]);

  useEffect(() => {
    fetchEvents(pagination.currentPage, searchTerm, filterCategory, filterActive);
  }, [searchTerm, filterCategory, filterActive]);

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        showNotification('Authentication required. Please login again.', 'error');
        return;
      }

      const response = await fetch(`${getApiBaseUrl()}/api/admin/events/${eventId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        showNotification(errorData.message || errorData.error || `Failed to delete event (${response.status})`, 'error');
        return;
      }

      const data = await response.json();

      if (data.success) {
        showNotification('Event deleted successfully!', 'success');
        const currentPage = pagination.currentPage;
        fetchEvents(currentPage, searchTerm, filterCategory, filterActive);
      } else {
        showNotification(data.message || data.error || 'Failed to delete event', 'error');
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      showNotification('Failed to delete event: ' + error.message, 'error');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'ไม่ระบุ';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateStr;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'raid': return 'bg-indigo-100 text-indigo-700';
      case 'spotlight': return 'bg-green-100 text-green-700';
      case 'max_monday': return 'bg-gray-100 text-gray-700';
      case 'event': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading && events.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div>
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Categories</option>
            {categories.length > 0 ? (
              categories.map((cat) => (
                <option key={cat.id} value={cat.name_en?.toLowerCase() || cat.name_th}>
                  {getCategoryName(cat, 'en')}
                </option>
              ))
            ) : (
              <>
                <option value="raid">Raid Battles</option>
                <option value="spotlight">Spotlight Hours</option>
                <option value="max_monday">Max Mondays</option>
                <option value="event">Events</option>
              </>
            )}
          </select>
          <select
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="active">แสดงเฉพาะ Active</option>
            <option value="inactive">แสดงเฉพาะ Inactive</option>
            <option value="all">แสดงทั้งหมด</option>
          </select>
          <button
            onClick={() => {
              setEditingEvent(null);
              setShowModal(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Event
          </button>
        </div>
      </div>

      {/* Events List */}
      {events.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <Calendar className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No events found</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating a new event.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {events.map((event) => (
            <motion.div
              key={event.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 rounded-lg overflow-hidden border-2 border-gray-200 bg-gray-100">
                        <img
                          src={event.image_url || '/images/pokemon/0000.webp'}
                          alt={event.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = '/images/pokemon/0000.webp';
                          }}
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(event.category)}`}>
                          {event.category}
                        </span>
                        {event.sub_category && event.sub_category !== 'none' && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            {event.sub_category}
                          </span>
                        )}
                        {event.is_active === 0 || event.is_active === false ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            Inactive
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            Active
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {event.title || event.title_th}
                      </h3>
                      {event.description && (
                        <p className="text-sm text-gray-600 mb-2">{event.description}</p>
                      )}
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>{formatDate(event.start_date)} - {formatDate(event.end_date)}</span>
                        {event.bonus_text && (
                          <span className="text-blue-600">• {event.bonus_text}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => {
                        setEditingEvent(event);
                        setShowModal(true);
                      }}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteEvent(event.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-8">
          <button
            onClick={() => fetchEvents(pagination.currentPage - 1, searchTerm, filterCategory, filterActive)}
            disabled={pagination.currentPage === 1}
            className="px-4 py-2 rounded-lg bg-white border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            ก่อนหน้า
          </button>
          <span className="px-4 py-2 text-gray-700">
            หน้า {pagination.currentPage} จาก {pagination.totalPages} (ทั้งหมด {pagination.totalItems} รายการ)
          </span>
          <button
            onClick={() => fetchEvents(pagination.currentPage + 1, searchTerm, filterCategory, filterActive)}
            disabled={pagination.currentPage >= pagination.totalPages}
            className="px-4 py-2 rounded-lg bg-white border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            ถัดไป
          </button>
        </div>
      )}

      {/* Event Form Modal */}
      {showModal && (
        <EventFormModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingEvent(null);
          }}
          event={editingEvent}
          categories={categories}
          onSuccess={() => {
            setShowModal(false);
            setEditingEvent(null);
            fetchEvents(pagination.currentPage, searchTerm, filterCategory, filterActive);
          }}
          showNotification={showNotification}
        />
      )}
    </div>
  );
};

// Categories Tab Component
const CategoriesTab = ({ showNotification }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false); // Filter inactive categories
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('adminToken');
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${getApiBaseUrl()}/api/admin/categories?include_inactive=true`, {
        headers,
        cache: 'no-cache'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        const categoriesArray = data.data || [];
        const validCategories = Array.isArray(categoriesArray) 
          ? categoriesArray.filter(cat => cat && typeof cat === 'object' && !Array.isArray(cat))
          : [];
        setCategories(validCategories);
      } else {
        setError(data.error || 'Failed to fetch categories');
        setCategories([]);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Network error: ' + err.message);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        showNotification('Authentication required. Please login again.', 'error');
        return;
      }

      const response = await fetch(`${getApiBaseUrl()}/api/admin/categories/${categoryId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        showNotification(errorData.message || errorData.error || `Failed to delete category (${response.status})`, 'error');
        return;
      }

      const data = await response.json();

      if (data.success) {
        showNotification('Category deleted successfully!', 'success');
        fetchCategories();
      } else {
        showNotification(data.message || data.error || 'Failed to delete category', 'error');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      showNotification('Failed to delete category: ' + error.message, 'error');
    }
  };

  const filteredCategories = categories.filter(cat => {
    if (!cat) return false;
    
    // Filter by active status (hide inactive by default)
    if (!showInactive && !cat.is_active) {
      return false;
    }
    
    // Filter by search term
    const search = searchTerm.toLowerCase();
    return (
      (cat.name_en && cat.name_en.toLowerCase().includes(search)) ||
      (cat.name_th && cat.name_th.toLowerCase().includes(search)) ||
      (cat.description_en && cat.description_en.toLowerCase().includes(search)) ||
      (cat.description_th && cat.description_th.toLowerCase().includes(search))
    );
  });

  if (loading && categories.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div>
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span>แสดง Inactive</span>
            </label>
          </div>
          <button
            onClick={() => {
              setEditingCategory(null);
              setShowModal(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Category
          </button>
        </div>
      </div>

      {/* Categories List */}
      {filteredCategories.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <Tag className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No categories found</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating a new category.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCategories.map((category) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-white rounded-lg shadow-sm border-2 hover:shadow-md transition-all ${
                category.is_active ? 'border-gray-200' : 'border-red-200 opacity-60'
              }`}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: category.color || '#3B82F6' }}
                      ></div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {getCategoryName(category, 'en')}
                      </h3>
                    </div>
                    {category.name_th && category.name_th !== category.name_en && (
                      <p className="text-sm text-gray-600">{getCategoryName(category, 'th')}</p>
                    )}
                  </div>
                  {!category.is_active && (
                    <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded">
                      Inactive
                    </span>
                  )}
                </div>

                {(category.description_en || category.description_th) && (
                  <p className="text-sm text-gray-600 mb-4">
                    {category.description_th || category.description_en}
                  </p>
                )}

                {(category.is_weekly || category.is_monthly) && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                      <Calendar className="w-4 h-4" />
                      <span className="font-medium">Recurring Schedule</span>
                    </div>
                    {category.is_weekly && category.day_of_week !== null && (
                      <div className="text-xs text-gray-600">
                        Weekly: {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][category.day_of_week]}
                      </div>
                    )}
                    {category.default_start_time && category.default_end_time && (
                      <div className="flex items-center gap-1 text-xs text-gray-600 mt-1">
                        <Clock className="w-3 h-3" />
                        {category.default_start_time.substring(0, 5)} - {category.default_end_time.substring(0, 5)}
                      </div>
                    )}
                  </div>
                )}

                {category.has_bonuses && category.bonuses && (
                  <div className="mb-4">
                    <div className="text-xs font-medium text-gray-700 mb-1">Bonuses:</div>
                    {Array.isArray(category.bonuses) ? (
                      <div className="space-y-1">
                        {category.bonuses.map((bonus, idx) => (
                          <div key={idx} className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                            {typeof bonus === 'string' ? bonus : (bonus.type || bonus.description || JSON.stringify(bonus))}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500">No bonuses defined</div>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-end space-x-2 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setEditingCategory(category);
                      setShowModal(true);
                    }}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteCategory(category.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Category Form Modal */}
      {showModal && (
        <CategoryFormModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingCategory(null);
          }}
          category={editingCategory}
          onSuccess={() => {
            setShowModal(false);
            setEditingCategory(null);
            fetchCategories();
          }}
          showNotification={showNotification}
        />
      )}
    </div>
  );
};

// Event Form Modal (simplified - reuse from original EventsManagement)
const EventFormModal = ({ isOpen, onClose, event, categories, onSuccess, showNotification }) => {
  const [formData, setFormData] = useState({
    title: '',
    title_th: '',
    description: '',
    description_th: '',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    category: 'event',
    sub_category: 'none',
    bonus_text: '',
    pokemon_id: '',
    custom_color: '#3B82F6',
    image: null,
    imagePreview: null,
    event_bonuses: [],
    raid_bosses: [],
    wild_encounters: [],
    egg_hatches: [],
    field_research: [],
    collection_challenges: []
  });
  const [contentBlocks, setContentBlocks] = useState([]); // Block-based content
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('basic'); // 'basic', 'content', 'pokemon'

  useEffect(() => {
    if (event) {
      // Parse JSON fields if they exist
      const parseJsonField = (field) => {
        if (!field) return [];
        if (typeof field === 'string') {
          try {
            return JSON.parse(field);
          } catch {
            return [];
          }
        }
        return Array.isArray(field) ? field : [];
      };

      // Parse content_blocks if exists
      let blocks = [];
      if (event.content_blocks) {
        if (typeof event.content_blocks === 'string') {
          try {
            blocks = JSON.parse(event.content_blocks);
          } catch (e) {
            console.error('Error parsing content_blocks:', e);
            blocks = [];
          }
        } else if (Array.isArray(event.content_blocks)) {
          blocks = event.content_blocks;
        }
      }

      setFormData({
        title: event.title || '',
        title_th: event.title_th || '',
        description: event.description_en || event.description || '',
        description_th: event.description_th || '',
        start_date: event.start_date || '',
        end_date: event.end_date || '',
        start_time: event.start_time || '',
        end_time: event.end_time || '',
        category: event.category || 'event',
        sub_category: event.sub_category || 'none',
        bonus_text: event.bonus_text || '',
        pokemon_id: event.pokemon_id || '',
        custom_color: event.custom_color || '#3B82F6',
        image: null,
        imagePreview: event.event_image || event.image_url || null,
        event_bonuses: parseJsonField(event.event_bonuses),
        raid_bosses: parseJsonField(event.raid_bosses),
        wild_encounters: parseJsonField(event.wild_encounters),
        egg_hatches: parseJsonField(event.egg_hatches),
        field_research: parseJsonField(event.field_research),
        collection_challenges: parseJsonField(event.collection_challenges)
      });
      setContentBlocks(blocks);
    } else {
      setFormData({
        title: '',
        title_th: '',
        description: '',
        description_th: '',
        start_date: '',
        end_date: '',
        start_time: '',
        end_time: '',
        category: 'event',
        sub_category: 'none',
        bonus_text: '',
        pokemon_id: '',
        custom_color: '#3B82F6',
        image: null,
        imagePreview: null,
        event_bonuses: [],
        raid_bosses: [],
        wild_encounters: [],
        egg_hatches: [],
        field_research: [],
        collection_challenges: []
      });
    }
  }, [event, isOpen]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        image: file,
        imagePreview: URL.createObjectURL(file)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Authentication required');
      }

      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      if (formData.title_th) formDataToSend.append('title_th', formData.title_th);
      if (formData.description) formDataToSend.append('description', formData.description);
      if (formData.description_th) formDataToSend.append('description_th', formData.description_th);
      formDataToSend.append('start_date', formData.start_date);
      formDataToSend.append('end_date', formData.end_date);
      if (formData.start_time) formDataToSend.append('start_time', formData.start_time);
      if (formData.end_time) formDataToSend.append('end_time', formData.end_time);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('sub_category', formData.sub_category);
      if (formData.bonus_text) formDataToSend.append('bonus_text', formData.bonus_text);
      if (formData.pokemon_id) formDataToSend.append('pokemon_id', formData.pokemon_id);
      if (formData.custom_color) formDataToSend.append('custom_color', formData.custom_color);
      
      // Content blocks (new block-based system)
      if (contentBlocks.length > 0) {
        formDataToSend.append('content_blocks', JSON.stringify(contentBlocks));
      }
      
      // JSON fields (legacy - for backward compatibility)
      if (formData.event_bonuses.length > 0) {
        formDataToSend.append('event_bonuses', JSON.stringify(formData.event_bonuses));
      }
      if (formData.raid_bosses.length > 0) {
        formDataToSend.append('raid_bosses', JSON.stringify(formData.raid_bosses));
      }
      if (formData.wild_encounters.length > 0) {
        formDataToSend.append('wild_encounters', JSON.stringify(formData.wild_encounters));
      }
      if (formData.egg_hatches.length > 0) {
        formDataToSend.append('egg_hatches', JSON.stringify(formData.egg_hatches));
      }
      if (formData.field_research.length > 0) {
        formDataToSend.append('field_research', JSON.stringify(formData.field_research));
      }
      if (formData.collection_challenges.length > 0) {
        formDataToSend.append('collection_challenges', JSON.stringify(formData.collection_challenges));
      }
      
      if (formData.image) {
        formDataToSend.append('image', formData.image);
      } else if (formData.imagePreview && !event) {
        formDataToSend.append('image_url', formData.imagePreview);
      }

      const method = event ? 'PUT' : 'POST';
      const url = event
        ? `${getApiBaseUrl()}/api/admin/events/${event.id}`
        : `${getApiBaseUrl()}/api/admin/events`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      const data = await response.json();

      if (data.success) {
        showNotification(event ? 'Event updated successfully!' : 'Event created successfully!', 'success');
        onSuccess();
      } else {
        throw new Error(data.error || data.message || 'Failed to save event');
      }
    } catch (err) {
      console.error('Error saving event:', err);
      setError(err.message || 'Failed to save event');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {event ? 'Edit Event' : 'Create Event'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title (English) *
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title (Thai)
              </label>
              <input
                type="text"
                value={formData.title_th}
                onChange={(e) => setFormData(prev => ({ ...prev, title_th: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date *
                </label>
                <input
                  type="date"
                  required
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date *
                </label>
                <input
                  type="date"
                  required
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time (e.g., 10:00)
                </label>
                <input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Time (e.g., 20:00)
                </label>
                <input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  required
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="event">Event</option>
                  <option value="raid">Raid Battle</option>
                  <option value="spotlight">Spotlight Hour</option>
                  <option value="max_monday">Max Monday</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.name_en?.toLowerCase() || cat.name_th}>
                      {getCategoryName(cat, 'en')}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sub Category (for Raids)
                </label>
                <select
                  value={formData.sub_category}
                  onChange={(e) => setFormData(prev => ({ ...prev, sub_category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={formData.category !== 'raid'}
                >
                  <option value="none">None</option>
                  <option value="mega">Mega</option>
                  <option value="5-star">5-Star</option>
                  <option value="shadow">Shadow</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (English) - Markdown supported
              </label>
              <textarea
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter event description in Markdown format..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (Thai) - Markdown supported
              </label>
              <textarea
                rows={4}
                value={formData.description_th}
                onChange={(e) => setFormData(prev => ({ ...prev, description_th: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="กรอกรายละเอียดกิจกรรมในรูปแบบ Markdown..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bonus Text (e.g., "2x XP Catch") - Legacy field
              </label>
              <input
                type="text"
                value={formData.bonus_text}
                onChange={(e) => setFormData(prev => ({ ...prev, bonus_text: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Event Bonuses Section */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Event Bonuses
                </label>
                <button
                  type="button"
                  onClick={() => {
                    const newBonus = prompt('Enter bonus text (e.g., "2× XP for catching"):');
                    if (newBonus) {
                      setFormData(prev => ({
                        ...prev,
                        event_bonuses: [...prev.event_bonuses, newBonus]
                      }));
                    }
                  }}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add Bonus
                </button>
              </div>
              <div className="space-y-2">
                {formData.event_bonuses.map((bonus, index) => (
                  <div key={index} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                    <input
                      type="text"
                      value={bonus}
                      onChange={(e) => {
                        const newBonuses = [...formData.event_bonuses];
                        newBonuses[index] = e.target.value;
                        setFormData(prev => ({ ...prev, event_bonuses: newBonuses }));
                      }}
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          event_bonuses: prev.event_bonuses.filter((_, i) => i !== index)
                        }));
                      }}
                      className="text-red-600 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {formData.event_bonuses.length === 0 && (
                  <p className="text-sm text-gray-500 italic">No bonuses added yet</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event Image (Hero Image for Detail Page)
              </label>
              <div className="flex items-center space-x-4">
                {formData.imagePreview && (
                  <img
                    src={formData.imagePreview}
                    alt="Preview"
                    className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                  />
                )}
                <label className="flex items-center px-4 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50">
                  <Upload className="w-4 h-4 mr-2" />
                  {formData.image ? 'Change Image' : 'Upload Image'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Image will be automatically converted to WebP format
              </p>
            </div>

            {/* Content Blocks Section (New Block-Based System) */}
            <div className="border-t pt-4">
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-800">Content Blocks</h3>
                  <button
                    type="button"
                    onClick={() => {
                      const newBlock = {
                        id: uuidv4(),
                        type: 'rich_text',
                        order: contentBlocks.length,
                        config: {
                          content_en: '',
                          content_th: ''
                        }
                      };
                      setContentBlocks([...contentBlocks, newBlock]);
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Rich Text Block
                  </button>
                </div>
                
                {contentBlocks.length === 0 ? (
                  <p className="text-sm text-gray-500 italic py-4">
                    No content blocks yet. Add a Rich Text block to get started.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {contentBlocks
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map((block, index) => (
                        <div key={block.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium text-gray-700">
                              Block {index + 1}: {block.type === 'rich_text' ? 'Rich Text' : block.type}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setContentBlocks(contentBlocks.filter(b => b.id !== block.id));
                              }}
                              className="text-red-600 hover:text-red-700 text-sm"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          
                          {block.type === 'rich_text' ? (
                            <RichTextBlockEditor
                              block={block}
                              onUpdate={(newConfig) => {
                                setContentBlocks(contentBlocks.map(b =>
                                  b.id === block.id
                                    ? { ...b, config: newConfig }
                                    : b
                                ));
                              }}
                            />
                          ) : (
                            <p className="text-sm text-gray-500 italic">
                              Block type "{block.type}" editor coming soon
                            </p>
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>

            {/* Collapsible Sections for Pokemon Data */}
            <div className="border-t pt-4">
              <details className="group">
                <summary className="cursor-pointer text-sm font-medium text-gray-700 mb-2 flex items-center justify-between">
                  <span>Pokemon Data (Raid Bosses, Wild Encounters, Eggs, etc.)</span>
                  <span className="text-gray-400 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="mt-4 space-y-4">
                  {/* Raid Bosses */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Raid Bosses
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          const tier = prompt('Enter tier (1-star, 3-star, 5-star, mega, shadow):');
                          const pokemonId = prompt('Enter Pokemon ID:');
                          const pokemonName = prompt('Enter Pokemon Name:');
                          const isShiny = window.confirm('Is Shiny Available?');
                          if (tier && pokemonId && pokemonName) {
                            setFormData(prev => ({
                              ...prev,
                              raid_bosses: [...prev.raid_bosses, {
                                tier,
                                pokemon_id: pokemonId,
                                pokemon_name: pokemonName,
                                is_shiny_available: isShiny
                              }]
                            }));
                          }
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Add Raid Boss
                      </button>
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {formData.raid_bosses.map((boss, index) => (
                        <div key={index} className="bg-gray-50 p-2 rounded text-sm">
                          <div className="flex items-center justify-between">
                            <span>{boss.tier} - {boss.pokemon_name} {boss.is_shiny_available ? '✨' : ''}</span>
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  raid_bosses: prev.raid_bosses.filter((_, i) => i !== index)
                                }));
                              }}
                              className="text-red-600 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                      {formData.raid_bosses.length === 0 && (
                        <p className="text-sm text-gray-500 italic">No raid bosses added yet</p>
                      )}
                    </div>
                  </div>

                  {/* Wild Encounters */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Wild Encounters
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          const pokemonId = prompt('Enter Pokemon ID:');
                          const pokemonName = prompt('Enter Pokemon Name:');
                          const isShiny = window.confirm('Is Shiny Available?');
                          if (pokemonId && pokemonName) {
                            setFormData(prev => ({
                              ...prev,
                              wild_encounters: [...prev.wild_encounters, {
                                pokemon_id: pokemonId,
                                pokemon_name: pokemonName,
                                is_shiny_available: isShiny,
                                rarity: 'common'
                              }]
                            }));
                          }
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Add Pokemon
                      </button>
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {formData.wild_encounters.map((encounter, index) => (
                        <div key={index} className="bg-gray-50 p-2 rounded text-sm">
                          <div className="flex items-center justify-between">
                            <span>{encounter.pokemon_name} {encounter.is_shiny_available ? '✨' : ''}</span>
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  wild_encounters: prev.wild_encounters.filter((_, i) => i !== index)
                                }));
                              }}
                              className="text-red-600 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                      {formData.wild_encounters.length === 0 && (
                        <p className="text-sm text-gray-500 italic">No wild encounters added yet</p>
                      )}
                    </div>
                  </div>

                  {/* Egg Hatches */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Egg Hatches
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          const eggType = prompt('Enter egg type (2km, 5km, 10km):');
                          const pokemonId = prompt('Enter Pokemon ID:');
                          const pokemonName = prompt('Enter Pokemon Name:');
                          const isShiny = window.confirm('Is Shiny Available?');
                          if (eggType && pokemonId && pokemonName) {
                            setFormData(prev => ({
                              ...prev,
                              egg_hatches: [...prev.egg_hatches, {
                                egg_type: eggType,
                                pokemon_id: pokemonId,
                                pokemon_name: pokemonName,
                                is_shiny_available: isShiny
                              }]
                            }));
                          }
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Add Egg
                      </button>
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {formData.egg_hatches.map((egg, index) => (
                        <div key={index} className="bg-gray-50 p-2 rounded text-sm">
                          <div className="flex items-center justify-between">
                            <span>{egg.egg_type} - {egg.pokemon_name} {egg.is_shiny_available ? '✨' : ''}</span>
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  egg_hatches: prev.egg_hatches.filter((_, i) => i !== index)
                                }));
                              }}
                              className="text-red-600 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                      {formData.egg_hatches.length === 0 && (
                        <p className="text-sm text-gray-500 italic">No egg hatches added yet</p>
                      )}
                    </div>
                  </div>

                  {/* Field Research */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Field Research
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          const task = prompt('Enter task (e.g., "Catch 10 Pokémon"):');
                          const reward = prompt('Enter reward:');
                          if (task && reward) {
                            setFormData(prev => ({
                              ...prev,
                              field_research: [...prev.field_research, {
                                task,
                                reward
                              }]
                            }));
                          }
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Add Task
                      </button>
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {formData.field_research.map((research, index) => (
                        <div key={index} className="bg-gray-50 p-2 rounded text-sm">
                          <div className="flex items-center justify-between">
                            <span>{research.task} → {research.reward}</span>
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  field_research: prev.field_research.filter((_, i) => i !== index)
                                }));
                              }}
                              className="text-red-600 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                      {formData.field_research.length === 0 && (
                        <p className="text-sm text-gray-500 italic">No field research tasks added yet</p>
                      )}
                    </div>
                  </div>
                </div>
              </details>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                {error}
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : event ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

// Category Form Modal (reuse from CategoryManagement)
const CategoryFormModal = ({ isOpen, onClose, category, onSuccess, showNotification }) => {
  const [formData, setFormData] = useState({
    name_en: '',
    name_th: '',
    description_en: '',
    description_th: '',
    color: '#3B82F6',
    is_active: true,
    default_start_time: '',
    default_end_time: '',
    is_weekly: false,
    day_of_week: null,
    is_monthly: false,
    is_multi_day: false,
    has_bonuses: false,
    bonuses: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bonusInput, setBonusInput] = useState('');

  useEffect(() => {
    if (category) {
      setFormData({
        name_en: category.name_en || '',
        name_th: category.name_th || '',
        description_en: category.description_en || '',
        description_th: category.description_th || '',
        color: category.color || '#3B82F6',
        is_active: category.is_active !== undefined ? category.is_active : true,
        default_start_time: category.default_start_time || '',
        default_end_time: category.default_end_time || '',
        is_weekly: category.is_weekly || false,
        day_of_week: category.day_of_week !== null ? category.day_of_week : null,
        is_monthly: category.is_monthly || false,
        is_multi_day: category.is_multi_day || false,
        has_bonuses: category.has_bonuses || false,
        bonuses: Array.isArray(category.bonuses) ? category.bonuses : (category.bonuses ? JSON.parse(category.bonuses) : [])
      });
    } else {
      setFormData({
        name_en: '',
        name_th: '',
        description_en: '',
        description_th: '',
        color: '#3B82F6',
        is_active: true,
        default_start_time: '',
        default_end_time: '',
        is_weekly: false,
        day_of_week: null,
        is_monthly: false,
        is_multi_day: false,
        has_bonuses: false,
        bonuses: []
      });
    }
  }, [category, isOpen]);

  const handleAddBonus = () => {
    if (bonusInput.trim()) {
      setFormData(prev => ({
        ...prev,
        bonuses: [...prev.bonuses, bonusInput.trim()]
      }));
      setBonusInput('');
    }
  };

  const handleRemoveBonus = (index) => {
    setFormData(prev => ({
      ...prev,
      bonuses: prev.bonuses.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Authentication required');
      }

      const payload = {
        ...formData,
        bonuses: formData.has_bonuses ? formData.bonuses : null
      };

      const method = category ? 'PUT' : 'POST';
      const url = category
        ? `${getApiBaseUrl()}/api/admin/categories/${category.id}`
        : `${getApiBaseUrl()}/api/admin/categories`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        showNotification(category ? 'Category updated successfully!' : 'Category created successfully!', 'success');
        onSuccess();
      } else {
        throw new Error(data.error || data.message || 'Failed to save category');
      }
    } catch (err) {
      console.error('Error saving category:', err);
      setError(err.message || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {category ? 'Edit Category' : 'Create Category'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name (English) *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name_en}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name (Thai)
                </label>
                <input
                  type="text"
                  value={formData.name_th}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_th: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (English)
                </label>
                <textarea
                  value={formData.description_en}
                  onChange={(e) => setFormData(prev => ({ ...prev, description_en: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (Thai)
                </label>
                <textarea
                  value={formData.description_th}
                  onChange={(e) => setFormData(prev => ({ ...prev, description_th: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={2}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color
                </label>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  className="w-full h-10 border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.is_active ? 'active' : 'inactive'}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === 'active' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Recurring Schedule</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_weekly}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_weekly: e.target.checked, is_monthly: e.target.checked ? false : prev.is_monthly }))}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Weekly</span>
                  </label>
                  {formData.is_weekly && (
                    <select
                      value={formData.day_of_week !== null ? formData.day_of_week : ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, day_of_week: e.target.value !== '' ? parseInt(e.target.value) : null }))}
                      className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select day</option>
                      {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, idx) => (
                        <option key={idx} value={idx}>{day}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_monthly}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_monthly: e.target.checked, is_weekly: e.target.checked ? false : prev.is_weekly }))}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Monthly</span>
                  </label>
                </div>
              </div>
              {(formData.is_weekly || formData.is_monthly) && (
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default Start Time
                    </label>
                    <input
                      type="time"
                      value={formData.default_start_time}
                      onChange={(e) => setFormData(prev => ({ ...prev, default_start_time: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default End Time
                    </label>
                    <input
                      type="time"
                      value={formData.default_end_time}
                      onChange={(e) => setFormData(prev => ({ ...prev, default_end_time: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="border-t pt-4">
              <label className="flex items-center mb-3">
                <input
                  type="checkbox"
                  checked={formData.has_bonuses}
                  onChange={(e) => setFormData(prev => ({ ...prev, has_bonuses: e.target.checked }))}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-gray-700">Has Bonuses</span>
              </label>
              {formData.has_bonuses && (
                <div>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={bonusInput}
                      onChange={(e) => setBonusInput(e.target.value)}
                      placeholder="Enter bonus (e.g., '2x XP Catch')"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddBonus();
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleAddBonus}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Add
                    </button>
                  </div>
                  {formData.bonuses.length > 0 && (
                    <div className="space-y-2">
                      {formData.bonuses.map((bonus, index) => (
                        <div key={index} className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded">
                          <span className="text-sm text-blue-900">{bonus}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveBonus(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                {error}
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {category ? 'Update' : 'Create'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default EventsManagementUnified;
